import { ComboboxSelect, Stack, Typography } from '@chronosphereio/chrono-ui';
import { invert } from 'lodash';
import { useMemo } from 'react';
import { Group } from '@/dashboards/components/Builder';
import { useLabelNames } from '../promql-builder-apis';
import { useBuilderContext } from '../promql-builder-context';
import {
  CALCULATION_KIND_TO_FUNCTION,
  Calculation,
  convertStringToQuantile,
  isAggregation,
  isAggregationKind,
  isClassicHistogramBucketMetric,
  isHistogramType,
  isPercentile,
  isPercentileKind,
  MetricMetadata,
} from '../promql-builder-model';

export const DEFAULT_QUANTILE = 0.99;

export function CalculationSelection() {
  const { calculation, onCalculationChange, hasCalculation } = useBuilderContext();

  return (
    <Group.Root onRemove={hasCalculation ? () => onCalculationChange(undefined) : undefined}>
      <Group.Label>Aggregation</Group.Label>
      <CalculationSelector />
      {isPercentile(calculation) && <QuantileSelector />}
      {hasCalculation && (
        <>
          <Group.Label isMuted>by</Group.Label>
          <LabelSelector />
        </>
      )}
    </Group.Root>
  );
}

/**
 * The `CalculationSelector` options depend on the metric.
 */
function useCalculationOptions() {
  const { metric, metricType, isClassicHistogramMetric } = useBuilderContext();

  return useMemo(() => {
    const optionToKind = getCalculationOptions(metric, { metricType, isClassicHistogramMetric });
    const options = Object.keys(optionToKind);
    const kindToOption = invert(optionToKind);

    return { options, optionToKind, kindToOption };
  }, [metric, metricType, isClassicHistogramMetric]);
}

function getCalculationOptions(metric: string, metricMetadata: MetricMetadata): Record<string, Calculation['kind']> {
  const { metricType, isClassicHistogramMetric } = metricMetadata;
  if (isClassicHistogramBucketMetric(metric, isClassicHistogramMetric)) {
    // Limit to histogram options
    return {
      Percentile: 'percentile',
    };
  } else if (isHistogramType(metricType)) {
    // Limit to histogram options
    return {
      Percentile: 'percentile',
      Average: 'histogramAverage',
      Count: 'histogramCount',
    };
  } else if (metricType) {
    // Limit to non-histogram options
    return {
      Sum: 'sum',
      Average: 'average',
      Min: 'min',
      Max: 'max',
      Count: 'count',
    };
  } else {
    // Show all options since we don't know metric type
    return {
      Sum: 'sum',
      Average: 'average',
      Min: 'min',
      Max: 'max',
      Count: 'count',
      Percentile: 'percentile',
      'Histogram average': 'histogramAverage',
      'Histogram count': 'histogramCount',
    };
  }
}

export function isValidCalculation(calculation: Calculation, metric: string, metricMetadata: MetricMetadata) {
  const validKinds = Object.values(getCalculationOptions(metric, metricMetadata));
  return validKinds.includes(calculation.kind);
}

function CalculationSelector() {
  const { calculation, onCalculationChange, hasCalculation } = useBuilderContext();
  const { options, optionToKind, kindToOption } = useCalculationOptions();

  // Fallback to function if we can't find an option
  const calculationValue = calculation
    ? (kindToOption[calculation.kind] ?? CALCULATION_KIND_TO_FUNCTION[calculation.kind])
    : null;

  return (
    <ComboboxSelect
      onReset={() => null}
      secondaryContent={({ highlightedOptionValue: option }) => {
        if (!option) return null;
        const kind = optionToKind[option];
        if (!kind) return null;
        return <CalculationDetails option={option} kind={kind} />;
      }}
      value={calculationValue}
      renderValue={
        <Group.Button isLast={!hasCalculation} isPlaceholder={!hasCalculation}>
          {calculationValue ?? 'No aggregation'}
        </Group.Button>
      }
      searchPlaceholder="Search functions"
      allowCustomValue={false}
      options={options}
      onSelect={(option) => {
        if (!option) {
          onCalculationChange(undefined);
        } else {
          const newKind = optionToKind[option];
          const newLabels = calculation && calculation.kind === newKind ? calculation.labels : [];
          if (isAggregationKind(newKind)) {
            onCalculationChange({
              kind: newKind,
              labels: isAggregation(calculation) ? calculation.labels : [],
            });
          } else if (isPercentileKind(newKind)) {
            onCalculationChange({
              kind: newKind,
              labels: newLabels,
              quantile: isPercentile(calculation) ? calculation.quantile : DEFAULT_QUANTILE,
            });
          } else if (newKind) {
            onCalculationChange({
              kind: newKind,
              labels: newLabels,
            });
          }
        }
      }}
    />
  );
}

function CalculationDetails({ option, kind }: { option: string; kind: Calculation['kind'] }) {
  return (
    <Stack spacing={1} padding={1} borderLeft="1px solid" borderColor="border.default" width="350px">
      <Typography variant="h3">{option}</Typography>
      <Typography variant="body2">
        {kind === 'sum' && (
          <>
            Calculates the sum of values over time. <br />
            Can be grouped by any label.
          </>
        )}
        {kind === 'average' && (
          <>
            Calculates the average of values over time. <br />
            Can be grouped by any label.
          </>
        )}
        {kind === 'min' && (
          <>
            Calculates the minimum value over time. <br />
            Can be grouped by any label.
          </>
        )}
        {kind === 'max' && (
          <>
            Calculates the maximum value over time. <br />
            Can be grouped by any label.
          </>
        )}
        {kind === 'count' && (
          <>
            Counts the number of time series. <br />
            Can be grouped by any label.
          </>
        )}
        {kind === 'percentile' && (
          <>
            Calculates a specific percentile from histogram data. <br />
          </>
        )}
        {kind === 'histogramAverage' && (
          <>
            Calculates the average value from histogram data. <br />
          </>
        )}
        {kind === 'histogramCount' && (
          <>
            Calculates total number of observations from histogram data. <br />
          </>
        )}
      </Typography>
    </Stack>
  );
}

const QUANTILE_OPTIONS = ['99', '95', '90', '75', '50'];

function QuantileSelector() {
  const { calculation, onCalculationChange } = useBuilderContext();

  if (!calculation || !isPercentile(calculation)) return null;

  const quantileStr = String(calculation.quantile * 100);

  return (
    <ComboboxSelect
      onReset={() => null}
      isMulti={false}
      value={quantileStr}
      renderValue={<Group.Button>{`${quantileStr}th`}</Group.Button>}
      searchPlaceholder="Search or enter a custom value (ex. 80)"
      allowCustomValue
      options={QUANTILE_OPTIONS}
      onSelect={(option) => {
        let newQuantile = DEFAULT_QUANTILE;
        try {
          if (option) {
            newQuantile = convertStringToQuantile(option);
          }
        } catch (_e) {
          // Do nothing and default to DEFAULT_QUANTILE
        }
        onCalculationChange({ ...calculation, quantile: newQuantile });
      }}
    />
  );
}

function LabelSelector() {
  const { metric, calculation, onCalculationChange } = useBuilderContext();

  const { data: options, isLoading } = useLabelNames(metric);

  if (!calculation) return null;

  const labels = calculation.labels ?? null;

  let labelStr, isPlaceholder;
  if (labels === null) {
    labelStr = '(nothing)';
    isPlaceholder = true;
  } else if (labels.length === 0) {
    labelStr = '(everything)';
    isPlaceholder = true;
  } else {
    labelStr = labels.join(', ');
    isPlaceholder = false;
  }

  return (
    <ComboboxSelect
      onReset={() => null}
      isMulti
      isLoading={isLoading}
      value={labels}
      renderValue={<Group.Button isPlaceholder={isPlaceholder}>{labelStr}</Group.Button>}
      searchPlaceholder="Search labels"
      allowCustomValue
      options={options}
      onSelect={(options) => {
        const newLabels = options ?? [];
        onCalculationChange({ ...calculation, labels: newLabels });
      }}
    />
  );
}
